/**
 * Cliente HTTP da API. Envia o cliente da plataforma (X-Tenant) e o login (Bearer).
 * Erros viram ApiError com a mensagem pronta para mostrar ao usuário.
 */
import { TENANT_SLUG } from './tenant';

const BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
const TOKEN_KEY = `dotapps-token-${TENANT_SLUG}`;
/** sessão do suporte guardada enquanto ele visualiza como outra pessoa */
const SUPPORT_KEY = `dotapps-token-suporte-${TENANT_SLUG}`;
const CARTEIRA_KEY = `dotapps-carteira-${TENANT_SLUG}`;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

let token: string | null = (() => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
})();

/** Chamado quando a API diz que o login expirou (o app volta para a tela de entrada). */
let onUnauthorized: () => void = () => {};
export const setUnauthorizedHandler = (fn: () => void) => (onUnauthorized = fn);

export function setToken(t: string | null) {
  token = t;
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* sem armazenamento: login vale só nesta aba */
  }
}

export const hasToken = () => !!token;

const read = (k: string): string | null => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k: string, v: string | null) => {
  try {
    if (v) localStorage.setItem(k, v);
    else localStorage.removeItem(k);
  } catch {
    /* sem armazenamento: vale só nesta aba */
  }
};

/** Suporte: guarda a própria sessão e passa a usar a de quem ele visualiza. */
export function beginViewAs(viewToken: string) {
  if (token && !read(SUPPORT_KEY)) write(SUPPORT_KEY, token);
  setToken(viewToken);
}

/** Volta para a sessão do suporte. Devolve falso se não havia sessão guardada. */
export function endViewAs(): boolean {
  const own = read(SUPPORT_KEY);
  write(SUPPORT_KEY, null);
  if (!own) return false;
  setToken(own);
  return true;
}

/**
 * Filtro de carteira do administrador (telas Negociação e Cadastros):
 * "all" (escritório inteiro), "me" ou o id de um vendedor. A API ignora para quem não é administrador.
 */
let carteira: string = read(CARTEIRA_KEY) || 'all';
const listeners = new Set<() => void>();
export const getCarteira = () => carteira;
export function setCarteira(v: string) {
  carteira = v || 'all';
  write(CARTEIRA_KEY, carteira === 'all' ? null : carteira);
  listeners.forEach(l => l());
}
export function subscribeCarteira(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** URL absoluta para imagens servidas pela API (logos). */
export function assetUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  return path.startsWith('http') ? path : `${BASE}${path}`;
}

async function request<T>(method: string, path: string, body?: unknown, filter?: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}/api${path}`, {
      method,
      headers: {
        'X-Tenant': TENANT_SLUG,
        ...(filter && filter !== 'all' ? { 'X-Carteira': filter } : {}),
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(
      'Sem conexão com o servidor. Se a página ficou parada um tempo, aguarde alguns segundos e tente de novo.',
      0,
    );
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 401 && token) {
      // visualização do suporte expirou: volta para a sessão dele em vez de sair
      if (endViewAs()) window.location.reload();
      else {
        setToken(null);
        onUnauthorized();
      }
    }
    const issues = data?.issues as Array<{ erro: string }> | undefined;
    const msg = issues?.length ? issues.map(i => i.erro).join('; ') : data?.message;
    throw new ApiError(typeof msg === 'string' ? msg : 'Não foi possível concluir. Tente de novo.', res.status);
  }
  return data as T;
}

export const api = {
  /** `carteira`: filtro do administrador (ver setCarteira) */
  get: <T>(path: string, carteira?: string) => request<T>('GET', path, undefined, carteira),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  del: <T = void>(path: string) => request<T>('DELETE', path),
};
